/**
 * Operations Hub reads (docs/specs/portal/operations-hub.md §17). The projects and the tracker positions come from
 * the Hub functions of `20261021050000_operations_hub.sql` (the staff role check; clients refused); each project's
 * plan and line status are read as the Progress page reads them (`loadProjectPlan`). Every rule is
 * `@teslyn/domain` `ops-hub.ts`.
 */
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  DEFAULT_TRACKING_SETTINGS,
  fleetMr,
  hubDeviceKey,
  type CloudTrafficStatus,
  type FleetMrRow,
  type PublicTrafficHubRow,
  type HubBlock,
  type HubContractArea,
  type HubFleetAircraft,
  type HubFlownAircraft,
  type HubPositionRow,
  type HubProjectInput,
  type HubReportRef,
  type ProjectPhoto,
  type TrackerAsset,
  type TrackingSettings,
  type VariationKm,
} from '@teslyn/domain';
import { must, pages, type Res } from './api.ts';

interface HubProjectRow {
  id: string;
  number: string;
  title: string;
  client_name: string | null;
  status: string;
  job_type: 'fixed_wing' | 'helicopter' | null;
  epsg: number | null;
  first_day: string | null;
  blocks: HubBlock[] | null;
  contract_areas: HubContractArea[] | null;
  contract_variations: VariationKm[] | null;
  contract_aircraft_type: string | null;
  contract_aircraft_regos: unknown[] | null;
  flown_aircraft: HubFlownAircraft[] | null;
  reports: HubReportRef[] | null;
  /** Decision 491: the project's stand-down period and its Suspended days (absent from an older database). */
  stand_down_from?: string | null;
  stand_down_to?: string | null;
  suspended_days?: unknown[] | null;
}

/** A project of the Hub with the day it began (its first daily entry or flight; the estimated finish's start). */
export interface HubProjectLoaded extends HubProjectInput {
  readonly firstDay: string | null;
}

const list = <T>(v: T[] | null | undefined): T[] => (Array.isArray(v) ? v : []);

export async function loadHubProjects(sb: SupabaseClient, tenantId: string): Promise<HubProjectLoaded[]> {
  const rows = must((await sb.rpc('ops_hub_projects', { p_tenant: tenantId })) as Res<HubProjectRow[]>) ?? [];
  return rows.map((r) => ({
    id: r.id,
    number: r.number,
    title: r.title,
    clientName: r.client_name ?? '',
    status: r.status,
    jobType: r.job_type === 'helicopter' ? 'helicopter' : 'fixed_wing',
    epsg: r.epsg,
    firstDay: typeof r.first_day === 'string' ? r.first_day.slice(0, 10) : null,
    blocks: list(r.blocks),
    contractAreas: Array.isArray(r.contract_areas) ? r.contract_areas : null,
    contractVariations: list(r.contract_variations),
    contractAircraftType: r.contract_aircraft_type,
    contractRegos: list(r.contract_aircraft_regos).filter((x): x is string => typeof x === 'string'),
    flown: list(r.flown_aircraft),
    reports: list(r.reports),
    standDownFrom: typeof r.stand_down_from === 'string' ? r.stand_down_from.slice(0, 10) : null,
    standDownTo: typeof r.stand_down_to === 'string' ? r.stand_down_to.slice(0, 10) : null,
    suspendedDays: list(r.suspended_days)
      .filter((d): d is string => typeof d === 'string')
      .map((d) => d.slice(0, 10)),
  }));
}

export async function loadHubFleet(sb: SupabaseClient, tenantId: string): Promise<HubFleetAircraft[]> {
  const rows =
    must(
      (await sb
        .from('aircraft')
        .select('id, registration, type_name')
        .eq('tenant_id', tenantId)
        .is('deleted_at', null)
        .order('registration')) as Res<{ id: string; registration: string; type_name: string | null }[]>,
    ) ?? [];
  return rows.map((r) => ({ id: r.id, registration: r.registration, type_name: r.type_name ?? '' }));
}

/**
 * The rows of the Hub's list of hours to the next MR (R239, `fleetMr`): the aircraft with their MR settings, the
 * projects' flags and every live daily entry that names an aircraft, as a project's Fuel and MR tab reads them.
 */
export async function loadHubMr(sb: SupabaseClient, tenantId: string): Promise<FleetMrRow[]> {
  type Plane = Parameters<typeof fleetMr>[0]['aircraft'][number];
  type Job = Parameters<typeof fleetMr>[0]['projects'][number];
  type Entry = Parameters<typeof fleetMr>[0]['entries'][number];
  const [aircraft, projects, entries] = await Promise.all([
    (async () =>
      must(
        (await sb
          .from('aircraft')
          .select('id, registration, mr_interval_h, ttis_at_issue_h, ttis_at_start_h')
          .eq('tenant_id', tenantId)
          .is('deleted_at', null)) as Res<Plane[]>,
      ) ?? [])(),
    (async () =>
      must(
        (await sb
          .from('project')
          .select('id, number, mr_warn_h')
          .eq('tenant_id', tenantId)
          .is('deleted_at', null)) as Res<Job[]>,
      ) ?? [])(),
    pages<Entry>(
      (from, to) =>
        sb
          .from('daily_entry')
          .select(
            'id, project_id, aircraft_id, deleted_at, date, takeoff_time, flt_num, svy_hours, cbx_hours, cal_hours, fry_hours, new_mr_ttis_h',
          )
          .eq('tenant_id', tenantId)
          .is('deleted_at', null)
          .not('aircraft_id', 'is', null)
          .order('date')
          .order('id')
          .range(from, to) as unknown as PromiseLike<Res<Entry[]>>,
    ),
  ]);
  return fleetMr({ aircraft, projects, entries });
}

export interface HubTrackingRows {
  readonly assets: TrackerAsset[];
  readonly rows: HubPositionRow[];
  /** Devices with an SOS active, by `hubDeviceKey` (provider, account label, device id). */
  readonly sos: Set<string>;
  readonly settings: TrackingSettings;
  /** The poller's words per tracker account (`tracker_poll_state`, decision 419), e.g. "V2Track: 7 aircraft, 3 positions stored". */
  readonly accounts: { readonly state: string; readonly message: string }[];
}

interface SettingsRow {
  lost_tracking_minutes: number | null;
  provider_minutes: TrackingSettings['provider_minutes'] | null;
  clients_see_public_traffic: boolean | null;
  source_priority: TrackingSettings['source_priority'] | null;
}

/**
 * The organisation's tracker assets and the positions of the last `hours` (1 to 48) the cloud poller stored for
 * every device of its tracking accounts (decisions 419, 420, 453), with SOS states by device and the lost-tracking
 * minutes. `projectId`: for a staff project map (decision 827), the positions come through
 * `project_tracker_positions` (the project's staff, field crew and data processors too; 20261027014000) and the
 * tracker accounts' words are not read.
 */
export async function loadHubTracking(
  sb: SupabaseClient,
  tenantId: string,
  hours: number,
  projectId: string | null = null,
): Promise<HubTrackingRows> {
  const [assets, rows, alerts, settings, accounts] = await Promise.all([
    (async () =>
      must(
        (await sb.from('tracker_asset').select('*').eq('tenant_id', tenantId).is('deleted_at', null)) as Res<
          TrackerAsset[]
        >,
      ) ?? [])(),
    pages<HubPositionRow>(
      (from, to) =>
        (projectId
          ? sb.rpc('project_tracker_positions', { p_project: projectId, p_hours: hours })
          : sb.rpc('ops_hub_positions', { p_tenant: tenantId, p_hours: hours })
        ).range(from, to) as unknown as PromiseLike<Res<HubPositionRow[]>>,
    ),
    (async () =>
      must(
        (await sb
          .from('tracker_alert_state')
          .select('provider, account_label, device_id, sos_active')
          .eq('tenant_id', tenantId)) as Res<
          { provider: string; account_label: string; device_id: string; sos_active: boolean }[]
        >,
      ) ?? [])(),
    (async () =>
      (must(
        (await sb
          .from('tracking_settings')
          .select('lost_tracking_minutes, provider_minutes, clients_see_public_traffic, source_priority')
          .eq('tenant_id', tenantId)
          .is('deleted_at', null)) as Res<SettingsRow[]>,
      ) ?? [])[0] ?? null)(),
    (async () =>
      projectId
        ? []
        : (must(
            (await sb
              .from('tracker_poll_state')
              .select('state, message')
              .eq('tenant_id', tenantId)
              .order('provider')) as Res<{ state: string; message: string }[]>,
          ) ?? []))(),
  ]);
  return {
    assets,
    rows,
    accounts,
    sos: new Set(
      alerts.filter((a) => a.sos_active).map((a) => hubDeviceKey(a.provider, a.account_label, a.device_id)),
    ),
    settings: settings
      ? {
          lost_tracking_minutes:
            settings.lost_tracking_minutes ?? DEFAULT_TRACKING_SETTINGS.lost_tracking_minutes,
          provider_minutes: settings.provider_minutes ?? {},
          clients_see_public_traffic:
            settings.clients_see_public_traffic ?? DEFAULT_TRACKING_SETTINGS.clients_see_public_traffic,
          source_priority: settings.source_priority ?? [],
        }
      : DEFAULT_TRACKING_SETTINGS,
  };
}

/** Public air traffic Teslyn Cloud asked around the active projects (parity C7, decision 627). */
export interface HubPublicTraffic {
  /** The organisation's switch and the poller's last words; null before Teslyn Cloud has the feature. */
  readonly status: CloudTrafficStatus | null;
  /** The positions of the last 2 hours, the trails (10 minutes before 20261027014000; none while it is off). */
  readonly rows: readonly PublicTrafficHubRow[];
}

/** A function Teslyn Cloud does not have yet (the migration not applied): PostgREST's PGRST202, Postgres 42883. */
const notThereYet = (e: { code?: string; message?: string } | null): boolean =>
  !!e &&
  (e.code === 'PGRST202' || e.code === '42883' || /could not find the function/i.test(e.message ?? ''));

/**
 * The Hub's public air traffic (`cloud_traffic_watch`, then `ops_hub_public_traffic`). Each read keeps the
 * organisation's traffic asked for while the Hub is open (the poller asks only then), and the first asks the poller
 * to run at once (20261027014000). `after`: only the rows stored after that row id (a map that has the trails). Before
 * the migration is applied there is nothing to show and nothing is said.
 */
export async function loadHubPublicTraffic(
  sb: SupabaseClient,
  tenantId: string,
  after: number | null = null,
): Promise<HubPublicTraffic> {
  const w = (await sb.rpc('cloud_traffic_watch', { p_tenant: tenantId })) as Res<CloudTrafficStatus>;
  if (notThereYet(w.error)) return { status: null, rows: [] };
  const status = must(w);
  if (!status?.enabled) return { status, rows: [] };
  const args = after === null ? { p_tenant: tenantId } : { p_tenant: tenantId, p_after: after };
  const rows = await pages<PublicTrafficHubRow>(
    (from, to) =>
      sb.rpc('ops_hub_public_traffic', args).range(from, to) as unknown as PromiseLike<
        Res<PublicTrafficHubRow[]>
      >,
  );
  return { status, rows };
}

/**
 * A staff project map's public air traffic (`project_public_traffic_watch`, then `project_public_traffic`;
 * 20261027014000): as the Hub's, for the staff of the project's organisation who can read the project (field crew
 * and data processors too). Never called for a client or Preview as client (decision 765). Before the migration is
 * applied there is nothing to show.
 */
export async function loadProjectPublicTraffic(
  sb: SupabaseClient,
  projectId: string,
  after: number | null = null,
): Promise<HubPublicTraffic> {
  const w = (await sb.rpc('project_public_traffic_watch', {
    p_project: projectId,
  })) as Res<CloudTrafficStatus>;
  if (notThereYet(w.error)) return { status: null, rows: [] };
  const status = must(w);
  if (!status?.enabled) return { status, rows: [] };
  const args = after === null ? { p_project: projectId } : { p_project: projectId, p_after: after };
  const rows = await pages<PublicTrafficHubRow>(
    (from, to) =>
      sb.rpc('project_public_traffic', args).range(from, to) as unknown as PromiseLike<
        Res<PublicTrafficHubRow[]>
      >,
  );
  return { status, rows };
}

/** One crew member on a project card (decision 422). */
export interface HubCrewMember {
  readonly name: string;
  /** Pilot, Operator, … from the staff register. */
  readonly role: string;
}

/**
 * The crew of each project (decision 422): the staff register's project crew in force today. Shell passes
 * `hubCrewLoader` (apps/web/src/staff/api.ts, the `project_crew_named` view) to OpsScreen, then HubView `loadCrew`.
 */
export type HubCrewLoader = (projectIds: readonly string[]) => Promise<ReadonlyMap<string, HubCrewMember[]>>;

/**
 * The tenant's live project photos that record a place, by project (decision 429: pins on the Hub map). Staff read
 * every photo of their tenant (RLS); a failure leaves the map without photo pins, never without the Hub.
 */
export async function loadHubPhotos(
  sb: SupabaseClient,
  tenantId: string,
): Promise<ReadonlyMap<string, ProjectPhoto[]>> {
  const res = (await sb
    .from('project_photo')
    .select('*')
    .eq('tenant_id', tenantId)
    .is('deleted_at', null)
    .not('taken_lat', 'is', null)) as Res<ProjectPhoto[]>;
  const out = new Map<string, ProjectPhoto[]>();
  for (const p of must(res) ?? []) out.set(p.project_id, [...(out.get(p.project_id) ?? []), p]);
  return out;
}
